import { c as _c } from "react/compiler-runtime";
import type { CreditCardOffer } from '@cc-shopping/contracts/compare/schema/types';
import { calculateStickyHeaderHeights } from '@cc-shopping/contracts/compare/utils/calculateStickyHeaderHeights';
import { BrowserStorageWrapper } from '@cc-shopping/platform-storage';
import { theme } from '@cc-shopping/ui/theme';
import { useMediaQuery } from '@mui/material';
import { type Dispatch, type SetStateAction, useEffect, useState } from 'react';
import { MAXIMUM_COLUMNS_DESKTOP, MAXIMUM_COLUMNS_MOBILE } from '../../ComparisonSection/ColumnContext';
import type { MarketplaceIds } from '../types';
import { DEFAULT_APPLY_NOW_MIN_HEIGHT, LOCAL_STORAGE_KEYS_MAP } from '../utils';
type Props = {
  allOffers: CreditCardOffer[];
  offersToShow: (CreditCardOffer | undefined)[];
  setOffersToShow: Dispatch<SetStateAction<(CreditCardOffer | undefined)[]>>;
  isLoading: boolean;
  idsToCompare: MarketplaceIds;
  setIdsToCompare: Dispatch<SetStateAction<MarketplaceIds>>;
  initialAnyLargeOfferTitle: boolean;
  initialAnyRatesAndFeesOffer: boolean;
  initialStickyHeaderMinHeights: {
    desktop: number;
    mobile: number;
  };
};
export function useUIUpdates(t0) {
  const $ = _c(38);
  const {
    allOffers,
    idsToCompare,
    offersToShow,
    setIdsToCompare,
    setOffersToShow,
    isLoading,
    initialAnyLargeOfferTitle,
    initialAnyRatesAndFeesOffer,
    initialStickyHeaderMinHeights
  } = t0;
  const [areSlotsOpen, setAreSlotsOpen] = useState(true);
  const [anyLargeOfferTitle, setAnyLargeOfferTitle] = useState(initialAnyLargeOfferTitle);
  const [anyRatesAndFeesOffer, setAnyRatesAndFeesOffer] = useState(initialAnyRatesAndFeesOffer);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [applyNowHeightsByIndex, setApplyNowHeightsByIndex] = useState(t1);
  const [tallestApplyNow, setTallestApplyNow] = useState(DEFAULT_APPLY_NOW_MIN_HEIGHT);
  const [stickyHeaderMinHeights, setStickyHeaderMinHeights] = useState(initialStickyHeaderMinHeights);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = theme.breakpoints.down("md");
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const isMobile = useMediaQuery(t2);
  const maxSlots = isMobile ? MAXIMUM_COLUMNS_MOBILE : MAXIMUM_COLUMNS_DESKTOP;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = new BrowserStorageWrapper();
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const storage = t3;
  let t4;
  if ($[3] !== applyNowHeightsByIndex || $[4] !== maxSlots) {
    t4 = () => {
      if (applyNowHeightsByIndex.length >= maxSlots) {
        let highestValue = 0;
        applyNowHeightsByIndex.slice(0, maxSlots).forEach(applyNowHeight => {
          if (applyNowHeight > highestValue) {
            highestValue = applyNowHeight;
          }
        });
        if (highestValue !== 0) {
          setTallestApplyNow(highestValue);
        }
      }
    };
    $[3] = applyNowHeightsByIndex;
    $[4] = maxSlots;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const calculateMaxApplyNowHeight = t4;
  let t5;
  if ($[6] !== isMobile) {
    t5 = newCompareIds => {
      if (isMobile && newCompareIds[2]) {
        const nullIndex = newCompareIds.indexOf(null);
        newCompareIds[nullIndex] = newCompareIds[2];
        newCompareIds[2] = null;
      }
    };
    $[6] = isMobile;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const moveOfferToAvailableSpot = t5;
  let t6;
  let t7;
  if ($[8] !== isLoading || $[9] !== offersToShow) {
    t6 = () => {
      const hasValidOffers = offersToShow.some(_temp);
      if (!isLoading && hasValidOffers) {
        const validOffers = offersToShow.filter(_temp2);
        const {
          anyLargeOfferTitle: newAnyLargeOfferTitle,
          anyRatesAndFeesOffer: newAnyRatesAndFeesOffer,
          stickyHeaderMinHeights: newStickyHeaderMinHeights
        } = calculateStickyHeaderHeights(validOffers);
        setAnyLargeOfferTitle(newAnyLargeOfferTitle);
        setAnyRatesAndFeesOffer(newAnyRatesAndFeesOffer);
        setStickyHeaderMinHeights(newStickyHeaderMinHeights);
      }
    };
    t7 = [offersToShow, isLoading];
    $[8] = isLoading;
    $[9] = offersToShow;
    $[10] = t6;
    $[11] = t7;
  } else {
    t6 = $[10];
    t7 = $[11];
  }
  useEffect(t6, t7);
  let t8;
  if ($[12] !== calculateMaxApplyNowHeight) {
    t8 = () => {
      calculateMaxApplyNowHeight();
    };
    $[12] = calculateMaxApplyNowHeight;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== applyNowHeightsByIndex) {
    t9 = [applyNowHeightsByIndex];
    $[14] = applyNowHeightsByIndex;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[16] !== idsToCompare || $[17] !== maxSlots) {
    t10 = () => {
      setAreSlotsOpen(idsToCompare.slice(0, maxSlots).includes(null));
    };
    t11 = [idsToCompare, maxSlots];
    $[16] = idsToCompare;
    $[17] = maxSlots;
    $[18] = t10;
    $[19] = t11;
  } else {
    t10 = $[18];
    t11 = $[19];
  }
  useEffect(t10, t11);
  let t12;
  if ($[20] !== allOffers || $[21] !== idsToCompare || $[22] !== isLoading || $[23] !== isMobile || $[24] !== offersToShow.length || $[25] !== setIdsToCompare || $[26] !== setOffersToShow) {
    t12 = () => {
      if (!isLoading) {
        if (isMobile) {
          setOffersToShow(_temp3);
          const nullIndex_0 = idsToCompare.indexOf(null);
          if (![-1, MAXIMUM_COLUMNS_DESKTOP - 1].includes(nullIndex_0)) {
            setIdsToCompare(prevIds => {
              const newIds = [...prevIds];
              newIds[nullIndex_0] = prevIds[MAXIMUM_COLUMNS_DESKTOP - 1];
              newIds[MAXIMUM_COLUMNS_DESKTOP - 1] = null;
              storage.setItem(LOCAL_STORAGE_KEYS_MAP.IDS_TO_COMPARE, JSON.stringify(newIds));
              return newIds;
            });
          }
        } else {
          if (offersToShow.length < MAXIMUM_COLUMNS_DESKTOP) {
            const lastId = idsToCompare[MAXIMUM_COLUMNS_DESKTOP - 1];
            setOffersToShow(prevValue_0 => [...prevValue_0, lastId ? allOffers.find(offer_1 => offer_1.id === lastId) : undefined]);
          }
        }
      }
    };
    $[20] = allOffers;
    $[21] = idsToCompare;
    $[22] = isLoading;
    $[23] = isMobile;
    $[24] = offersToShow.length;
    $[25] = setIdsToCompare;
    $[26] = setOffersToShow;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== isMobile) {
    t13 = [isMobile];
    $[28] = isMobile;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  useEffect(t12, t13);
  let t14;
  if ($[30] !== anyLargeOfferTitle || $[31] !== anyRatesAndFeesOffer || $[32] !== areSlotsOpen || $[33] !== calculateMaxApplyNowHeight || $[34] !== moveOfferToAvailableSpot || $[35] !== stickyHeaderMinHeights || $[36] !== tallestApplyNow) {
    t14 = {
      areSlotsOpen,
      anyLargeOfferTitle,
      anyRatesAndFeesOffer,
      tallestApplyNow,
      setApplyNowHeightsByIndex,
      calculateMaxApplyNowHeight,
      stickyHeaderMinHeights,
      moveOfferToAvailableSpot
    };
    $[30] = anyLargeOfferTitle;
    $[31] = anyRatesAndFeesOffer;
    $[32] = areSlotsOpen;
    $[33] = calculateMaxApplyNowHeight;
    $[34] = moveOfferToAvailableSpot;
    $[35] = stickyHeaderMinHeights;
    $[36] = tallestApplyNow;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  return t14;
}
function _temp3(prevValue) {
  return prevValue.slice(0, MAXIMUM_COLUMNS_MOBILE);
}
function _temp2(offer_0) {
  return offer_0 !== undefined;
}
function _temp(offer) {
  return offer !== undefined;
}